import React, { useState } from 'react';
import { TrendingUp, DollarSign, Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UnitSelector } from '../components/UnitSelector';
import { floorTypes, calculateROI } from '../mockData';
import { useToast } from '../hooks/use-toast';
import { useUnits } from '../hooks/useUnits';

export const ROICalculator = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { unit, changeUnit, convertToM2, getUnitLabel } = useUnits();
  
  const [formData, setFormData] = useState({
    area: '',
    comparisonType: 'concrete',
    years: '10'
  });
  const [roiData, setRoiData] = useState(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    
    if (!formData.area || parseFloat(formData.area) <= 0) {
      toast({
        title: t('roi.invalidArea'),
        description: t('roi.enterValidArea'),
        variant: "destructive"
      });
      return;
    }

    // Convert area to m² for calculation
    const areaInM2 = convertToM2(parseFloat(formData.area));
    
    const result = calculateROI(
      areaInM2,
      formData.comparisonType,
      parseInt(formData.years)
    );
    
    // Store with display unit info
    setRoiData({
      ...result,
      displayUnit: unit,
      displayArea: parseFloat(formData.area)
    });
    
    toast({
      title: t('roi.analysisComplete'),
      description: t('roi.projectedSavings', { savings: result.savings.total.toLocaleString(), years: formData.years }),
    });
  };

  const comparisonOptions = floorTypes.filter(f => f.id !== 'epoxy');

  return (
    <div className="min-h-screen pt-20 bg-slate-50">
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('roi.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('roi.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Calculator Form */}
        <Card className="shadow-xl mb-8">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle className="text-2xl">{t('roi.comparisonParams')}</CardTitle>
              <UnitSelector unit={unit} onUnitChange={changeUnit} />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCalculate} className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="area">{t('roi.area')} ({getUnitLabel()})</Label>
                <Input
                  id="area"
                  type="number"
                  placeholder={unit === 'sqft' ? 'Ex: 2152' : t('roi.areaPlaceholder')}
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  min="1"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="comparisonType">Compare with</Label>
                <Select
                  value={formData.comparisonType}
                  onValueChange={(value) => setFormData({ ...formData, comparisonType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {comparisonOptions.map((floor) => (
                      <SelectItem key={floor.id} value={floor.id}>
                        {floor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="years">Period (years)</Label>
                <Select
                  value={formData.years}
                  onValueChange={(value) => setFormData({ ...formData, years: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 years</SelectItem>
                    <SelectItem value="10">10 years</SelectItem>
                    <SelectItem value="15">15 years</SelectItem>
                    <SelectItem value="20">20 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Calculate ROI
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {roiData && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl">
                <CardContent className="p-6">
                  <DollarSign className="w-10 h-10 mb-3 opacity-90" />
                  <div className="text-3xl font-bold mb-1">
                    ${roiData.savings.total.toLocaleString()}
                  </div>
                  <div className="text-green-100">Total Savings</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-xl">
                <CardContent className="p-6">
                  <TrendingUp className="w-10 h-10 mb-3 opacity-90" />
                  <div className="text-3xl font-bold mb-1">
                    {roiData.savings.roi}%
                  </div>
                  <div className="text-blue-100">ROI</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-xl">
                <CardContent className="p-6">
                  <Calendar className="w-10 h-10 mb-3 opacity-90" />
                  <div className="text-3xl font-bold mb-1">
                    {roiData.savings.breakEvenYear || 'N/A'}
                  </div>
                  <div className="text-amber-100">
                    {roiData.savings.breakEvenYear ? 'Years to Break Even' : 'Always Lower'}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-xl">
                <CardContent className="p-6">
                  <DollarSign className="w-10 h-10 mb-3 opacity-90" />
                  <div className="text-3xl font-bold mb-1">
                    ${roiData.savings.perYear.toLocaleString()}
                  </div>
                  <div className="text-purple-100">Average Annual Savings</div>
                </CardContent>
              </Card>
            </div>

            {/* Comparison Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Epoxy Card */}
              <Card className="shadow-xl border-2 border-green-500">
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-2xl text-green-800 flex items-center">
                    <CheckCircle2 className="w-6 h-6 mr-2" />
                    {roiData.epoxy.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Initial Investment</span>
                    <span className="font-bold text-slate-900">
                      ${roiData.epoxy.initialCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Maintenance ({roiData.years} years)</span>
                    <span className="font-bold text-slate-900">
                      ${roiData.epoxy.maintenanceTotal.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Replacements needed</span>
                    <span className="font-bold text-slate-900">
                      {roiData.epoxy.replacements} ({roiData.epoxy.lifespan} years lifespan)
                    </span>
                  </div>
                  {roiData.epoxy.replacementCosts > 0 && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-600">Replacement cost</span>
                      <span className="font-bold text-slate-900">
                        ${roiData.epoxy.replacementCosts.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-4 mt-4">
                    <span className="font-bold text-green-900">Total Cost</span>
                    <span className="text-2xl font-bold text-green-600">
                      ${roiData.epoxy.totalCost.toLocaleString()}
                    </span>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-semibold text-slate-800 mb-3">Benefits:</h4>
                    <ul className="space-y-2">
                      {roiData.epoxy.benefits.map((benefit, idx) => (
                        <li key={`epoxy-benefit-${idx}-${benefit.substring(0, 10)}`} className="flex items-start space-x-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Comparison Card */}
              <Card className="shadow-xl border-2 border-slate-300">
                <CardHeader className="bg-slate-50">
                  <CardTitle className="text-2xl text-slate-800 flex items-center">
                    <AlertCircle className="w-6 h-6 mr-2" />
                    {roiData.comparison.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Initial Investment</span>
                    <span className="font-bold text-slate-900">
                      ${roiData.comparison.initialCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Maintenance ({roiData.years} years)</span>
                    <span className="font-bold text-slate-900">
                      ${roiData.comparison.maintenanceTotal.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Replacements needed</span>
                    <span className="font-bold text-slate-900">
                      {roiData.comparison.replacements} ({roiData.comparison.lifespan} years lifespan)
                    </span>
                  </div>
                  {roiData.comparison.replacementCosts > 0 && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-600">Replacement cost</span>
                      <span className="font-bold text-slate-900">
                        ${roiData.comparison.replacementCosts.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 bg-slate-100 rounded-lg px-4 mt-4">
                    <span className="font-bold text-slate-900">Total Cost</span>
                    <span className="text-2xl font-bold text-slate-600">
                      ${roiData.comparison.totalCost.toLocaleString()}
                    </span>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-semibold text-slate-800 mb-3">Benefits:</h4>
                    <ul className="space-y-2">
                      {roiData.comparison.benefits.map((benefit, idx) => (
                        <li key={`comparison-benefit-${idx}-${benefit.substring(0, 10)}`} className="flex items-start space-x-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Yearly Progression Table */}
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Cost Progression by Year</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-800">Year</th>
                        <th className="text-right py-3 px-4 font-semibold text-green-700">Epoxy</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">{roiData.comparison.name}</th>
                        <th className="text-right py-3 px-4 font-semibold text-blue-700">Accumulated Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roiData.yearlyData.map((data) => (
                        <tr key={data.year} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{data.year}</td>
                          <td className="text-right py-3 px-4 text-green-700">
                            ${data.epoxy.toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4 text-slate-700">
                            ${data.comparison.toLocaleString()}
                          </td>
                          <td className={`text-right py-3 px-4 font-semibold ${
                            data.savings >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {data.savings >= 0 ? '+' : ''}${data.savings.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Summary Box */}
            <Card className="shadow-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-green-900 mb-4">Analysis Summary</h3>
                <div className="space-y-3 text-slate-700">
                  <p className="text-lg">
                    By investing in <span className="font-bold text-green-700">epoxy flooring</span> instead of{' '}
                    <span className="font-semibold">{roiData.comparison.name.toLowerCase()}</span>, you will save{' '}
                    <span className="font-bold text-green-700 text-xl">${roiData.savings.total.toLocaleString()}</span>{' '}
                    over {roiData.years} years.
                  </p>
                  <p>
                    This represents a <span className="font-bold text-green-700">{roiData.savings.percentage}%</span> savings
                    compared to the total cost of {roiData.comparison.name.toLowerCase()}.
                  </p>
                  {roiData.savings.breakEvenYear && (
                    <p>
                      Your investment will pay for itself in approximately{' '}
                      <span className="font-bold text-green-700">{roiData.savings.breakEvenYear} year{roiData.savings.breakEvenYear > 1 ? 's' : ''}</span>,
                      after which you will continue saving.
                    </p>
                  )}
                  <p className="mt-4 pt-4 border-t border-green-200">
                    <span className="font-semibold">Average annual savings:</span>{' '}
                    <span className="font-bold text-green-700 text-lg">${roiData.savings.perYear.toLocaleString()}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Section */}
        {!roiData && (
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                Calculate Your Return on Investment
              </h3>
              <p className="text-slate-600 mb-4">
                Complete the data above to see a detailed cost comparison
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-left">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-2">Initial Investment</h4>
                  <p className="text-sm text-slate-600">
                    Compare installation costs between different floor types
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-2">Maintenance</h4>
                  <p className="text-sm text-slate-600">
                    Consider maintenance costs over time
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-2">Lifespan</h4>
                  <p className="text-sm text-slate-600">
                    Evaluate how many times you'll need to replace the floor
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
};
